import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { graphql } from 'gatsby';

import { useSingleToneDividerTextData } from './hooks';

import { filterProps } from 'utils/helpers';
import { RichText } from 'components/Prismic';
import LineClamp from 'components/LineClamp';
import { Normalize } from 'utils/types';

export type TSingleToneDividerTextSection =
  Normalize<Queries.SolutionDesktopSingleToneDividerTextData_PrismicSolutionDataDesktopSolutionSingleToneDividerText_Fragment>;

enum SINGLE_TONE_DIVIDER_TEXT_VARIABLES {
  titleColor = '--title-color',
  textColor = '--text-color',
}

const Container = styled('div', filterProps('isDark'))<{
  isDark: boolean;
}>`
  ${({ theme: { colors, mq, spacings }, isDark }) => css`
    ${SINGLE_TONE_DIVIDER_TEXT_VARIABLES.titleColor}: ${isDark ? colors.primary : colors.black};
    ${SINGLE_TONE_DIVIDER_TEXT_VARIABLES.textColor}: ${isDark ? colors.white : '#505050'};

    display: grid;
    position: relative;
    align-content: center;
    justify-items: center;
    background-color: ${isDark ? colors.backgroundDark : colors.offWhite};
    height: 139.625rem;

    ${mq.md} {
      padding: 8rem ${spacings.padding} 8.375rem;
      height: initial;

      && {
        display: grid;
      }
    }
  `}
`;

const Title = styled(RichText)`
  ${({ theme: { mq, tp } }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;

    &::after {
      display: block;
      margin: 5rem 0;
      background-color: var(${SINGLE_TONE_DIVIDER_TEXT_VARIABLES.textColor});
      width: 11.5rem;
      height: 0.25rem;
      content: '';
    }

    & > h2 {
      text-align: center;
      line-height: 0.965;
      letter-spacing: -0.04em;
      color: var(${SINGLE_TONE_DIVIDER_TEXT_VARIABLES.titleColor});
      font-size: 9rem;

      ${tp.useFont(fonts => fonts.Switzer.light)};

      & strong {
        ${tp.useFont(fonts => fonts.Switzer.bold)};
      }
    }

    ${mq.md} {
      display: inline;
      text-align: center;

      &::after {
        margin: 4rem auto;
        width: 5.75rem;
        height: 0.125rem;
      }

      & > h2 {
        display: inline;
        font-size: 4rem;

        br {
          display: none;
        }
      }
    }
  `}
`;

const Description = styled(RichText)`
  ${({ theme: { mq, tp } }) => css`
    display: flex;
    position: relative;

    & > p {
      text-align: center;
      line-height: 1.2;
      color: var(${SINGLE_TONE_DIVIDER_TEXT_VARIABLES.textColor});
      font-size: 2.75rem;

      ${tp.useFont(fonts => fonts.Switzer.lightest)};
    }

    ${mq.md} {
      & > p {
        text-align: left;
        font-size: 2rem;

        br {
          display: none;
        }
      }
    }
  `}
`;

const SingleToneDividerTextSection: React.FC<TSingleToneDividerTextSection> = sliceData => {
  const { isDark, title, description } = useSingleToneDividerTextData(sliceData);

  return (
    <Container isDark={isDark}>
      <Title render={title} />
      <LineClamp isDark={!isDark}>
        <Description render={description} />
      </LineClamp>
    </Container>
  );
};

export const desktopSingleToneDividerTextFragment = graphql`
  fragment SolutionDesktopSingleToneDividerTextData on PrismicSolutionDataDesktopSlicesType {
    ... on PrismicSolutionDataDesktopSolutionSingleToneDividerText {
      slice_type

      primary {
        solution_single_tone_divider_text_is_dark
        solution_single_tone_divider_text_title {
          richText
        }
        solution_single_tone_divider_text_description {
          richText
        }
      }
    }
  }
`;

export default SingleToneDividerTextSection;
